import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { Link } from "@ui/primitives/Link";
import InstagramSvg from "./assets/instagram.svg?react";
import LinkedInSvg from "./assets/linkedin.svg?react";
import WordmarkSvg from "./assets/wordmark.svg?react";

const CURRENT_YEAR = new Date().getFullYear();

const footer = tv({
  slots: {
    root: "flex flex-col items-center gap-6 bg-green-950 lg:gap-4",
    wordmark: "h-7 w-auto text-white",
    topRow: "flex flex-col items-center gap-4 lg:flex-row lg:w-full lg:justify-end lg:gap-2.5",
    nav: "flex items-center gap-2.5",
    navLink: "text-base text-white whitespace-nowrap",
    dot: "hidden size-[3px] shrink-0 rounded-full bg-white lg:block",
    divider: "w-full border-t border-white/20",
    legal: "flex flex-col items-center gap-3 lg:flex-row lg:w-full lg:gap-2.5",
    legalText: "text-base text-white/75 whitespace-nowrap",
    bottomRow: "flex flex-col items-center gap-3 lg:ml-auto lg:flex-row lg:gap-3",
    prefs: "flex items-center gap-3",
    socials: "flex items-center gap-3",
    socialIcon: "size-6 text-white",
    prefButton:
      "flex cursor-pointer items-center gap-1.5 text-base text-white outline-none hover:text-white/80 focus-visible:ring-2 focus-visible:ring-white/50 rounded",
  },
  variants: {
    /** Default: rounded "card" look with breathing-room margin above. Used on standard
     *  pages where the content area is constrained and the footer sits inside that frame.
     *  Full-bleed: edge-to-edge slab with no top margin and no rounded corners — for
     *  split-view / full-width pages (marketplace, destinations) where the footer should
     *  align with the page edges. Internal px stays at `px-8` to match the topbar. */
    fullBleed: {
      true: { root: "px-8 py-5 lg:py-6" },
      false: { root: "mt-5 p-5 rounded-t-3xl lg:mt-8 lg:rounded-t-[15px] lg:px-8 lg:py-6" },
    },
  },
  defaultVariants: {
    fullBleed: false,
  },
});

/** Site-wide footer with navigation links, legal text, and social icons. */
export interface FooterProps {
  /** Additional CSS class name. */
  className?: string;
  /** Label for the locale button, e.g. "English (GB)". */
  localeLabel?: string;
  /** Label for the currency button, e.g. "£ GBP". */
  currencyLabel?: string;
  /** Called when the locale button is pressed. */
  onLocalePress?: () => void;
  /** Called when the currency button is pressed. */
  onCurrencyPress?: () => void;
  /** Render edge-to-edge with no rounded top corners or top margin. Used on full-width
   *  / split-view pages (marketplace, destinations) where the footer aligns with the
   *  viewport edges. Default `false` (rounded "card" footer). */
  fullBleed?: boolean;
}

export function Footer({
  className,
  localeLabel,
  currencyLabel,
  onLocalePress,
  onCurrencyPress,
  fullBleed = false,
}: FooterProps) {
  const styles = footer({ fullBleed });

  return (
    <footer className={styles.root({ className })}>
      <div className={styles.topRow()}>
        <Link href="/" aria-label={t("components.footer.home")} className="lg:mr-auto">
          <WordmarkSvg aria-hidden="true" className={styles.wordmark()} />
        </Link>

        <nav aria-label={t("components.footer.navigation")} className={styles.nav()}>
          <Link href="/about" className={styles.navLink()}>
            {t("components.footer.about")}
          </Link>
          <span aria-hidden="true" className="size-[3px] shrink-0 rounded-full bg-white" />
          <Link href="https://livetravily.com/squiggle" target="_blank" className={styles.navLink()}>
            {t("components.footer.squiggle")}
          </Link>
        </nav>
      </div>

      <hr className={styles.divider()} />

      <div className={styles.legal()}>
        <p className={styles.legalText()}>{t("components.footer.copyright", { year: CURRENT_YEAR })}</p>
        <span aria-hidden="true" className={styles.dot()} />
        <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" className={styles.legalText()}>
          {t("components.footer.privacy_policy")}
        </Link>
        <span aria-hidden="true" className={styles.dot()} />
        <Link href="/cookie-policy" target="_blank" rel="noopener noreferrer" className={styles.legalText()}>
          {t("components.footer.cookie_policy")}
        </Link>

        <div className={styles.bottomRow()}>
          {(localeLabel || currencyLabel) && (
            <div className={styles.prefs()}>
              {localeLabel && (
                <Button className={styles.prefButton()} onPress={onLocalePress}>
                  <Icon name="globe" size="20" />
                  {localeLabel}
                </Button>
              )}
              {currencyLabel && (
                <Button className={styles.prefButton()} onPress={onCurrencyPress}>
                  {currencyLabel}
                </Button>
              )}
            </div>
          )}
          <div className={styles.socials()}>
            <Link
              href="https://instagram.com/travilylife"
              target="_blank"
              aria-label={t("components.footer.instagram")}
            >
              <InstagramSvg aria-hidden="true" className={styles.socialIcon()} />
            </Link>
            <Link
              href="https://linkedin.com/company/travily"
              target="_blank"
              aria-label={t("components.footer.linkedin")}
            >
              <LinkedInSvg aria-hidden="true" className={styles.socialIcon()} />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
